// utils.ts
import { CategoryPerformanceDataItem, Category, Label } from './types';

export const processCategoryData = (
  performanceData: CategoryPerformanceDataItem[],
  visualLabels: string[]
): Category[] => {
  // Group performance data by category
  const categoryMap = new Map<string, CategoryPerformanceDataItem[]>();

  performanceData.forEach((item) => {
    if (!categoryMap.has(item.category_name)) {
      categoryMap.set(item.category_name, []);
    }
    categoryMap.get(item.category_name)!.push(item);
  });

  // Convert to Category array
  const categories: Category[] = [];

  categoryMap.forEach((items, categoryName) => {
    let totalSpend = [...items].reduce(
      (total, item) => (total += item.total_spend ?? 0),
      0
    );

    // Sort labels by performance (CPA)
    const sortedItems = [...items].sort((a, b) => {
      // Lower CPA is better
      if (a.cpa === 0 && b.cpa === 0) return 0;
      if (a.cpa === 0) return 1;
      if (b.cpa === 0) return -1;
      return a.cpa - b.cpa;
    });

    const labels: Label[] = sortedItems.map((item, index) => {
      const isActive = visualLabels.includes(item.original_label);
      const percentSpend = item.total_spend / totalSpend;

      return {
        name: item.label_name,
        performance: { ...item, percentSpend },
        isActive,
        rank: index + 1,
      };
    });

    const currentLabel = labels.find((label) => label.isActive);

    categories.push({
      name: categoryName,
      labels,
      currentLabel,
    });
  });

  // Sort categories by importance
  return sortCategories(categories);
};

const sortCategories = (categories: Category[]): Category[] => {
  const priorityOrder = ['Brand', 'Format', 'Price', 'Promo'];

  return categories.sort((a, b) => {
    const aIndex = priorityOrder.indexOf(a.name);
    const bIndex = priorityOrder.indexOf(b.name);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.name.localeCompare(b.name);
  });
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

export const getPerformanceClass = (rank: number, total: number): string => {
  const percentile = rank / total;
  if (percentile <= 0.2) return 'top-performer';
  if (percentile <= 0.4) return 'good-performer';
  if (percentile <= 0.6) return 'average-performer';
  if (percentile <= 0.8) return 'below-average';
  return 'poor-performer';
};
